import { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchPartnerUsers } from "../../services/PartnerService";
import { canManagePartnerDetails } from "../../utils/auth/accessRules";
import { PERMISSION_SLUGS } from "../../utils/auth/permissionConstants";
import { eligibilityFor } from "./partnerUserEligibility";
import PartnerUserActionsModal from "./Modals/PartnerUserActionsModal";
import { usePartnerHasSoloUser } from "../../hooks/usePartnerHasSoloUser";

const ROLE_OPTIONS = [
  { value: "",            label: "All roles" },
  { value: "super-admin", label: "Super admin" },
  { value: "accountant",  label: "Accountant" },
  { value: "user",        label: "User" },
];

const STATUS_OPTIONS = [
  { value: "",          label: "All statuses" },
  { value: "active",    label: "Active" },
  { value: "inactive",  label: "Inactive" },
  { value: "pending",   label: "Pending" },
  { value: "suspended", label: "Suspended" },
];

const statusBadgeClass = (status) => {
  const value = status?.toLowerCase();
  if (value === "active")    return "badge-success";
  if (value === "pending")   return "badge-warning";
  if (value === "inactive")  return "badge-danger";
  if (value === "suspended") return "badge-light";
  return "badge-secondary";
};

/**
 * Shared users table used on the Organization page and on the standalone Users
 * page. Owns its own filter, search, and pagination state. Row actions and the
 * "Add user" button are opt-in through callbacks so the callers keep control of
 * their modals.
 */
const PREVIEW_PAGE_SIZE = 5;

const PartnerUsersTable = ({
  partnerId,
  authUser,
  userPartnerDetails,
  headerTitle = "Users",
  cardRef,
  mode = "full",
  onSeeAll,
  onAddUser,
  onViewInfo,
  onManageTransactionLimits,
  onEditUser,
  onActivateWallet,
  onAssignTeam,
  onChangeRole,
  onDeleteUser,
  onResendVerification,
  onFixEmail,
  teamFilter = null,
  onClearTeamFilter,
}) => {
  const isPreview = mode === "preview";
  // showTeamReviewerChips gates on both the resolved solo flag AND the
  // loading state so a solo user does not briefly see team-reviewer
  // chips during the check load window. The chips are meaningless in a
  // solo context and reading them momentarily would be misleading.
  const { hasSoloUser, isLoading: hasSoloUserLoading } = usePartnerHasSoloUser(partnerId);
  const showTeamReviewerChips = !hasSoloUserLoading && !hasSoloUser;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [actionsUser, setActionsUser] = useState(null);

  useEffect(() => {
    if (isPreview) return;
    const timer = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput, isPreview]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter]);

  const {
    data,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: isPreview
      ? ["partnerUsers", partnerId, "preview", PREVIEW_PAGE_SIZE]
      : ["partnerUsers", partnerId, currentPage, searchTerm, roleFilter, statusFilter, teamFilter?.id ?? ""],
    queryFn: () => (isPreview
      ? fetchPartnerUsers(partnerId, 1, "", "", "", PREVIEW_PAGE_SIZE)
      : fetchPartnerUsers(partnerId, currentPage, searchTerm, roleFilter, statusFilter, null, teamFilter?.id ?? "")),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const partnerUsers = data?.data?.data ?? null;
  const meta = data?.data?.meta ?? null;

  const goToPage = (nextPage) => {
    setCurrentPage(nextPage);
    if (cardRef?.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const canManageUsers = canManagePartnerDetails(
    authUser,
    userPartnerDetails,
    PERMISSION_SLUGS.MANAGE_PARTNER_USERS
  );
  const canManageTransactionLimits = canManagePartnerDetails(
    authUser,
    userPartnerDetails,
    PERMISSION_SLUGS.MANAGE_USER_TRANSACTION_LIMITS
  );

  const currentRoleLabel = ROLE_OPTIONS.find((o) => o.value === roleFilter)?.label ?? "All roles";
  const currentStatusLabel = STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ?? "All statuses";

  const usersHaveFilters = !!(searchTerm || roleFilter || statusFilter);

  const eligibilityContext = {
    authUser,
    canManageUsers,
    canManageTransactionLimits,
    actions: {
      onViewInfo,
      onManageTransactionLimits,
      onEditUser,
      onActivateWallet,
      onAssignTeam,
      onChangeRole,
      onDeleteUser,
      onResendVerification,
      onFixEmail,
    },
  };

  const renderUserActions = (partnerUser) => {
    const { hasAny } = eligibilityFor(partnerUser, eligibilityContext);
    if (!hasAny) return null;

    return (
      <button
        type="button"
        className="btn sharp btn-primary tp-btn i-false"
        aria-label={`Actions for ${partnerUser.full_name}`}
        data-tour="users-row-actions"
        onClick={() => setActionsUser(partnerUser)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18px" height="18px" viewBox="0 0 24 24">
          <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <rect x="0" y="0" width="24" height="24" />
            <circle fill="#000000" cx="12" cy="5" r="2" />
            <circle fill="#000000" cx="12" cy="12" r="2" />
            <circle fill="#000000" cx="12" cy="19" r="2" />
          </g>
        </svg>
      </button>
    );
  };

  return (
    <div className="card" ref={cardRef} style={{ scrollMarginTop: "5rem" }} data-tour="users-card">
      <div className="card-header d-block">
        <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
          <h4 className="card-title mb-0">{headerTitle}</h4>
          {onAddUser && canManageUsers && (
            <>
              <button
                type="button"
                className="btn btn-primary d-none d-lg-inline-flex ms-auto"
                onClick={onAddUser}
                data-tour="users-add-button"
              >
                Add User
              </button>
              <button
                type="button"
                className="btn btn-primary rounded-circle d-inline-flex d-lg-none align-items-center justify-content-center shadow-sm ms-auto"
                style={{ width: "2.5rem", height: "2.5rem", padding: 0 }}
                title="Add user"
                aria-label="Add user"
                onClick={onAddUser}
                data-tour="users-add-button"
              >
                <i className="fas fa-plus" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
        {!isPreview && (
          <div className="d-flex flex-wrap gap-2">
            <input
              type="text"
              className="form-control form-control-sm flex-grow-1"
              placeholder="Search by name, email or phone"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ minWidth: "200px" }}
              data-tour="users-search"
            />
            <Dropdown>
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                className="d-inline-flex align-items-center"
                style={{ minWidth: "140px" }}
                data-tour="users-role-filter"
              >
                <span className="flex-grow-1 text-start">{currentRoleLabel}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {ROLE_OPTIONS.map((option) => (
                  <Dropdown.Item
                    key={option.value || "all"}
                    active={roleFilter === option.value}
                    onClick={() => setRoleFilter(option.value)}
                  >
                    {option.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown>
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                className="d-inline-flex align-items-center"
                style={{ minWidth: "140px" }}
                data-tour="users-status-filter"
              >
                <span className="flex-grow-1 text-start">{currentStatusLabel}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {STATUS_OPTIONS.map((option) => (
                  <Dropdown.Item
                    key={option.value || "all"}
                    active={statusFilter === option.value}
                    onClick={() => setStatusFilter(option.value)}
                  >
                    {option.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
        {!isPreview && teamFilter && showTeamReviewerChips && (
          <div className="d-flex justify-content-end mt-2">
            <span
              className="d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-pill"
              style={{
                background: "var(--rgba-primary-1, rgba(100, 24, 195, 0.1))",
                color: "var(--primary, #6418C3)",
                border: "1px solid var(--rgba-primary-2, rgba(100, 24, 195, 0.25))",
              }}
            >
              <i className="fas fa-filter" aria-hidden="true" />
              Team: {teamFilter.name}
              {onClearTeamFilter && (
                <button
                  type="button"
                  onClick={onClearTeamFilter}
                  className="btn btn-sm p-0 ms-1 border-0 bg-transparent"
                  style={{ color: "inherit", lineHeight: 1 }}
                  aria-label="Clear team filter"
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              )}
            </span>
          </div>
        )}
        {!isPreview && statusFilter === "suspended" && canManageUsers && (
          <div className="d-flex justify-content-end mt-2">
            <Link
              to="/suspended-users"
              className="d-inline-flex align-items-center gap-2 fw-semibold text-decoration-none px-3 py-2 rounded-pill"
              style={{
                background: "#ffc107",
                color: "#5a3f00",
                border: "1px solid #d39e00",
                boxShadow: "0 2px 8px rgba(255, 193, 7, 0.35)",
              }}
            >
              <i className="bi bi-clock-history" aria-hidden="true" />
              View suspension details
              <i className="fas fa-chevron-right" aria-hidden="true" />
            </Link>
          </div>
        )}
      </div>
      <div className="card-body p-0" style={{ position: "relative" }}>
        {isFetching && partnerUsers && partnerUsers.length > 0 && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 10,
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            paddingTop: "1rem",
          }}>
            <span className="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
        )}
        {isLoading ? (
          <div className="d-flex justify-content-center py-4">
            <span className="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
        ) : !partnerUsers || partnerUsers.length === 0 ? (
          <p className="text-center text-muted py-4 mb-0">
            {usersHaveFilters ? "No users match the current filters." : "No users."}
          </p>
        ) : (
          <>
            <div className="table-responsive d-none d-xl-block px-3 pt-2">
              <table className="table verticle-middle mb-0">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Role</th>
                    <th scope="col">Email</th>
                    <th scope="col">Phone</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partnerUsers.map((partnerUser) => (
                    <tr key={partnerUser.id}>
                      <td style={{ overflowWrap: "anywhere" }}>
                        {partnerUser.full_name}
                        {partnerUser.id === authUser?.id && (
                          <span className="badge badge-rounded badge-info ms-2">You</span>
                        )}
                        {partnerUser.is_team_reviewer && showTeamReviewerChips && (
                          <span className="badge badge-rounded badge-primary ms-2">Team reviewer</span>
                        )}
                      </td>
                      <td>{partnerUser.role}</td>
                      <td style={{ overflowWrap: "anywhere" }}>{partnerUser.email}</td>
                      <td>{partnerUser.phone || <span className="text-muted">Not set</span>}</td>
                      <td>
                        <span className={`badge badge-rounded ${statusBadgeClass(partnerUser.status)}`}>
                          {partnerUser.status}
                        </span>
                      </td>
                      <td className="text-end">{renderUserActions(partnerUser)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-responsive d-none d-lg-block d-xl-none px-3 pt-2">
              <table className="table verticle-middle mb-0">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Role</th>
                    <th scope="col">Contact</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partnerUsers.map((partnerUser) => (
                    <tr key={partnerUser.id}>
                      <td style={{ overflowWrap: "anywhere" }}>
                        <span className="fw-semibold text-body">{partnerUser.full_name}</span>
                        {partnerUser.id === authUser?.id && (
                          <span className="badge badge-rounded badge-info ms-2">You</span>
                        )}
                        {partnerUser.is_team_reviewer && showTeamReviewerChips && (
                          <span className="badge badge-rounded badge-primary ms-2">Team reviewer</span>
                        )}
                      </td>
                      <td>{partnerUser.role}</td>
                      <td style={{ overflowWrap: "anywhere" }}>
                        <div>{partnerUser.email}</div>
                        {partnerUser.phone && (
                          <div className="small text-muted">{partnerUser.phone}</div>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-rounded ${statusBadgeClass(partnerUser.status)}`}>
                          {partnerUser.status}
                        </span>
                      </td>
                      <td className="text-end">{renderUserActions(partnerUser)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="list-group list-group-flush d-block d-lg-none">
              {partnerUsers.map((partnerUser) => (
                <li
                  key={partnerUser.id}
                  className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-nowrap py-3"
                >
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-1">
                      <span className="fw-semibold text-body" style={{ overflowWrap: "anywhere" }}>
                        {partnerUser.full_name}
                      </span>
                      {partnerUser.id === authUser?.id && (
                        <span className="badge badge-rounded badge-info">You</span>
                      )}
                      {partnerUser.is_team_reviewer && showTeamReviewerChips && (
                        <span className="badge badge-rounded badge-primary">Team reviewer</span>
                      )}
                      <span className={`badge badge-rounded ${statusBadgeClass(partnerUser.status)}`}>
                        {partnerUser.status}
                      </span>
                    </div>
                    <div className="small text-muted">{partnerUser.role}</div>
                    <div className="small text-muted" style={{ overflowWrap: "anywhere" }}>
                      {partnerUser.email}
                    </div>
                    {partnerUser.phone && (
                      <div className="small text-muted">{partnerUser.phone}</div>
                    )}
                  </div>
                  <div className="flex-shrink-0">{renderUserActions(partnerUser)}</div>
                </li>
              ))}
            </ul>
          </>
        )}
        {!isPreview && meta && meta.last_page > 1 && (
          <div className="d-flex justify-content-center align-items-center gap-3 py-3 border-top" data-tour="users-pagination">
            <button
              type="button"
              className="btn btn-sm btn-link text-decoration-none p-0"
              onClick={() => goToPage(meta.current_page - 1)}
              disabled={meta.current_page <= 1}
              aria-label="Previous page"
            >
              <i className="fas fa-chevron-left" aria-hidden="true" />
            </button>
            <span className="small text-muted">
              Page {meta.current_page} of {meta.last_page}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-link text-decoration-none p-0"
              onClick={() => goToPage(meta.current_page + 1)}
              disabled={meta.current_page >= meta.last_page}
              aria-label="Next page"
            >
              <i className="fas fa-chevron-right" aria-hidden="true" />
            </button>
          </div>
        )}
        {isPreview && onSeeAll && (
          <div className="d-flex justify-content-center py-3 border-top">
            <button
              type="button"
              className="btn btn-sm btn-link text-decoration-none"
              onClick={onSeeAll}
            >
              See all users
              <i className="fas fa-chevron-right ms-1" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      <PartnerUserActionsModal
        partnerUser={actionsUser}
        onClose={() => setActionsUser(null)}
        authUser={authUser}
        canManageUsers={canManageUsers}
        canManageTransactionLimits={canManageTransactionLimits}
        onViewInfo={onViewInfo}
        onManageTransactionLimits={onManageTransactionLimits}
        onEditUser={onEditUser}
        onActivateWallet={onActivateWallet}
        onAssignTeam={onAssignTeam}
        onChangeRole={onChangeRole}
        onDeleteUser={onDeleteUser}
        onResendVerification={onResendVerification}
        onFixEmail={onFixEmail}
      />
    </div>
  );
};

export default PartnerUsersTable;
